import React, { useEffect, useState, useRef } from 'react';
import { localToday, dateStr } from '../utils/dates';
import { useNavigate } from 'react-router-dom';
import {
  collection, getDocs, query, orderBy, doc,
  updateDoc, getDoc, setDoc, where
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Card, CardContent, Button, Chip,
  CircularProgress, Table, TableContainer, TableBody, TableCell, TableHead, TableRow,
  IconButton, Tooltip, Grid, Collapse, Alert, Select, MenuItem, FormControl,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { SHARE_ORIGIN, copyText } from '../utils/shareLink';
import { releaseTournamentCourts } from '../utils/eventCourtHolds';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import PaletteIcon from '@mui/icons-material/Palette';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PaymentsIcon from '@mui/icons-material/Payments';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { authedFetch } from '../utils/authedFetch';

function fmtDate(iso) {
  if (!iso) return '–';
  const [y,m,d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[+m-1]} ${+d}, ${y}`;
}

function loadJSZip() {
  return new Promise((resolve, reject) => {
    if (window.JSZip) { resolve(window.JSZip); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
    script.onload = () => resolve(window.JSZip);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function TournamentAdminDashboard() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  // Which row last had its link copied, so the icon can confirm quietly
  // instead of firing a snackbar for something this small.
  const [copiedId, setCopiedId] = useState(null);
  const [copyError, setCopyError] = useState('');

  const copyShareLink = async (t) => {
    const url = `${SHARE_ORIGIN}/t/${t.slug}`;
    if (await copyText(url)) {
      setCopyError('');
      setCopiedId(t.id);
      setTimeout(() => setCopiedId(c => (c === t.id ? null : c)), 2000);
    } else {
      setCopyError(`Couldn't copy automatically — the link is ${url}`);
    }
  };
  const [stats, setStats] = useState({ regs: 0, revenue: 0 });
  const [infoHtml, setInfoHtml] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [loading, setLoading] = useState(true);
  const quillRef = useRef(null);
  // Tournament results import (vb-scores)
  const [importDialog, setImportDialog] = useState(false);
  const [importTournament, setImportTournament] = useState(null);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState(null);

  // Payment links. For the sales the app cannot host — a team paying by phone,
  // a walk-up on the morning of. Made here rather than in the Stripe dashboard
  // so the link carries source=tournament_link and the money names itself in
  // the revenue report instead of being guessed at from the product wording.
  const [payDialog, setPayDialog] = useState(false);
  const [payTournament, setPayTournament] = useState(null);
  const [payDivision, setPayDivision] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payNote, setPayNote] = useState('');
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState('');
  const [payResult, setPayResult] = useState(null);
  const [payCopied, setPayCopied] = useState(false);

  // The division's own entry fee, which is the amount wanted almost every time.
  function priceForDivision(t, division) {
    const p = (t?.prices || {})[division];
    return p == null ? '' : String(p);
  }

  function openPayDialog(t) {
    const first = (t.divisions || [])[0] || '';
    setPayTournament(t);
    setPayDivision(first);
    setPayAmount(priceForDivision(t, first));
    setPayNote('');
    setPayError('');
    setPayResult(null);
    setPayCopied(false);
    setPayDialog(true);
  }

  function changePayDivision(division) {
    setPayDivision(division);
    // Follow the division's fee, but never overwrite an amount typed by hand.
    const suggested = priceForDivision(payTournament, division);
    if (suggested) setPayAmount(suggested);
    setPayResult(null);
  }

  async function createPaymentLink() {
    const amount = Number(payAmount);
    if (!Number.isFinite(amount) || amount < 0.5) {
      setPayError('Enter an amount of at least $0.50.');
      return;
    }
    setPayBusy(true);
    setPayError('');
    try {
      const fn = httpsCallable(getFunctions(), 'createTournamentPaymentLink');
      const { data } = await fn({
        tournamentId: payTournament.id,
        division: payDivision,
        amountDollars: amount,
        note: payNote.trim(),
      });
      setPayResult(data);
      // Keep the row in step so reopening the dialog shows the link.
      setTournaments(prev => prev.map(x => x.id === payTournament.id
        ? { ...x, payment_links: [
            ...((x.payment_links || []).filter(l => (l.division || '') !== payDivision)),
            { division: payDivision, url: data.url, id: data.id,
              amountCents: Math.round(amount * 100), active: true },
          ] }
        : x));
    } catch (e) {
      setPayError(e.message || 'Could not create the link.');
    } finally {
      setPayBusy(false);
    }
  }

  async function copyPayLink(url) {
    if (await copyText(url)) {
      setPayCopied(true);
      setTimeout(() => setPayCopied(false), 2000);
    } else {
      setPayError(`Couldn't copy automatically — the link is ${url}`);
    }
  }

  function openImportDialog(t) {
    setImportTournament(t);
    setImportUrl(t.vb_scores_url || '');
    setImportError('');
    setImportResult(null);
    setImportDialog(true);
  }

  async function handleImportResults() {
    if (!importUrl.trim()) { setImportError('Please paste a vb-scores URL.'); return; }
    setImporting(true);
    setImportError('');
    setImportResult(null);
    try {
      const res = await authedFetch('https://us-central1-torch-beach-app.cloudfunctions.net/importTournamentResults', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tournament_id: importTournament.id, vb_scores_url: importUrl.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setImportError(data.error || 'Import failed.'); return; }
      setImportResult(data);
      // Reflect the saved URL on the local tournaments list
      setTournaments(prev => prev.map(t => t.id === importTournament.id ? { ...t, vb_scores_url: importUrl.trim() } : t));
    } catch (e) {
      setImportError('Import failed: ' + (e.message || 'Unknown error'));
    } finally {
      setImporting(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (!editorOpen) return;
    let cancelled = false;
    loadQuill().then(Quill => {
      if (cancelled || quillRef.current) return;
      quillRef.current = new Quill('#quill-dashboard', {
        theme: 'snow',
        placeholder: 'Enter tournament details – date, location, schedule, rules...',
        modules: { toolbar: [[{ header: [1,2,3,false] }],['bold','italic','underline'],[{ color:[] },{ background:[] }],[{ align:[] }],['link'],[{ list:'ordered' },{ list:'bullet' }],['clean']] }
      });
      if (infoHtml) quillRef.current.clipboard.dangerouslyPasteHTML(infoHtml);
    });
    return () => { cancelled = true; };
  }, [editorOpen]);

  async function loadAll() {
    try {
      // Newest first: this page is worked from the top, and the events that
      // need attention are the ones just run or about to be. Ascending put
      // March Sandness at the top and this weekend's tournament at the bottom.
      const tSnap = await getDocs(query(collection(db, 'tournaments'), orderBy('date', 'desc')));
      setTournaments(tSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const rSnap = await getDocs(query(collection(db, 'registrations'),
        where('payment_status', 'in', ['paid', 'half_paid']), where('status', '==', 'active')));
      let revenue = 0;
      rSnap.docs.forEach(d => { revenue += d.data().amount_paid || 0; });
      setStats({ regs: rSnap.size, revenue: revenue / 100 });
      const infoSnap = await getDoc(doc(db, 'settings', 'tournamentInfo'));
      if (infoSnap.exists()) setInfoHtml(infoSnap.data().html || '');
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function saveInfoHtml() {
    setSavingInfo(true);
    try {
      let html = quillRef.current?.root.innerHTML || '';
      if (html === '<p><br></p>') html = '';
      await setDoc(doc(db, 'settings', 'tournamentInfo'), { html });
      setInfoHtml(html);
      setEditorOpen(false);
      quillRef.current = null;
    } catch(e) { alert('Save failed: ' + e.message); }
    finally { setSavingInfo(false); }
  }

  async function togglePublish(t) {
    await updateDoc(doc(db, 'tournaments', t.id), { active: !t.active });
    setTournaments(ts => ts.map(x => x.id === t.id ? { ...x, active: !x.active } : x));
  }

function getTournamentStatus(t) {
  if (t.tournament_status && t.tournament_status.startsWith('canceled')) return t.tournament_status;
  if (t.archived) return 'archived';
  const today = localToday();
  if (t.date && t.date < today && t.active) return 'complete';
  if (t.active) return 'scheduled';
  return 'pending';
}

const STATUS_CONFIG = {
  scheduled:         { label: 'Scheduled',         color: 'success' },
  pending:           { label: 'Pending',            color: 'warning' },
  complete:          { label: 'Complete',           color: 'default' },
  archived:          { label: 'Archived',           color: 'default' },
  'canceled-weather':  { label: 'Canceled (Weather)',   color: 'error' },
  'canceled-lowreg':   { label: 'Canceled (Low Reg)',   color: 'error' },
  'canceled-other':    { label: 'Canceled (Other)',     color: 'error' },
};

async function archiveTournament(t) {
  if (!window.confirm(`Archive "${t.name}"?\n\nThis will remove it from the public list and cancel all registrations. Stripe refunds must be processed manually.`)) return;
  try {
    // Archive the tournament
    await updateDoc(doc(db, 'tournaments', t.id), {
      archived: true,
      active: false,
    });
    // Cancel all active registrations
    const regSnap = await getDocs(query(
      collection(db, 'registrations'),
      where('tournament_id', '==', t.id),
      where('status', '==', 'active')
    ));
    for (const regDoc of regSnap.docs) {
      await updateDoc(doc(db, 'registrations', regDoc.id), {
        status: 'cancelled',
        cancelled_reason: 'tournament_archived',
      });
    }
    // Delete court reservations for this tournament
    const freed = await releaseTournamentCourts(db, t.id);
    alert(`Tournament archived. ${regSnap.docs.length} registration(s) cancelled. ${freed} court reservation(s) removed.`);
    setTournaments(ts => ts.map(x => x.id === t.id ? { ...x, archived: true, active: false } : x));
  } catch(err) {
    alert('Archive failed: ' + err.message);
  }
}

  async function exportAllCSV(t) {
    try {
      const snap = await getDocs(query(collection(db, 'registrations'),
        where('tournament_id', '==', t.id), where('status', '==', 'active')));
      const regs = snap.docs.map(d => d.data());
      const divs = Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]');
      const JSZip = await loadJSZip();
      const zip = new JSZip();
      divs.forEach(div => {
        const divRegs = regs.filter(r => r.division === div);
        if (divRegs.length === 0) return;
        const lines = ['Names,Points'];
        divRegs.forEach(r => {
          const p1 = (r.first_name || '').trim();
          const p2First = (r.partner_name || '').split(' ')[0] || '';
          lines.push(`"${p1}${p2First ? ' / ' + p2First : ''}",""`);
        });
        zip.file(`${div.replace(/\s+/g,'_')}.csv`, lines.join('\n'));
      });
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${t.name.replace(/\s+/g,'_')}_all_divisions.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('Export failed: ' + e.message); }
  }

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  const quickActions = [
    { label: 'Add Tournament', icon: <AddCircleIcon />, path: '/tournament-admin/tournament/add', color: 'primary' },
    { label: 'Divisions', icon: <AccountTreeIcon />, path: '/tournament-admin/divisions', color: 'primary' },
    { label: 'Leagues', icon: <SportsVolleyballIcon />, path: '/tournament-admin/leagues', color: 'primary' },
    { label: 'Registrations', icon: <PeopleIcon />, path: '/tournament-admin/registrations', color: 'primary' },
    { label: 'Financial', icon: <BarChartIcon />, path: '/tournament-admin/financial', color: 'primary' },
    { label: 'Branding', icon: <PaletteIcon />, path: '/tournament-admin/branding', color: 'primary' },
    { label: 'Export CSV', icon: <DownloadIcon />, path: '/tournament-admin/export-csv', color: 'primary' },
  ];

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Typography variant="h4" fontWeight="bold">
            ⚙️ Admin Dashboard
          </Typography>
        </Container>
      </Box>
      <Container sx={{ pb: 6 }}>

        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { label: 'Tournaments', value: tournaments.length, color: 'primary.main' },
            { label: 'Paid Registrations', value: stats.regs, color: 'success.main' },
            { label: 'Total Revenue', value: `$${stats.revenue.toFixed(2)}`, color: 'warning.main' },
          ].map(s => (
            <Grid size={{ xs: 12, sm: 4 }} key={s.label}>
              <Card elevation={2} sx={{ borderRadius: 3, textAlign: 'center', py: 2 }}>
                <Typography variant="h3" fontWeight="bold" color={s.color}>{s.value}</Typography>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Quick actions */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
          {quickActions.map(a => (
            <Button key={a.label} variant="contained" startIcon={a.icon}
                    onClick={() => navigate(a.path)}>{a.label}</Button>
          ))}
        </Box>

        {/* Info page editor */}
        <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography fontWeight="bold">📄 Tournament Info Page</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {infoHtml
                ? <Typography variant="caption" color="success.main">✓ Active</Typography>
                : <Typography variant="caption" color="text.secondary">Showing default date/location</Typography>
              }
              <Button size="small" variant="outlined" endIcon={editorOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      onClick={() => setEditorOpen(o => !o)}>
                Edit
              </Button>
            </Box>
          </Box>
          <Collapse in={editorOpen}>
            <CardContent sx={{ p: 3 }}>
              <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.min.css" />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Default info page copied into every new tournament. Leave blank to show date/location.
              </Typography>
              {/* Token picker. Selecting a token inserts {{tokenName}} at the
                  cursor position. Tokens are replaced with the live tournament
                  value on the public tournament page (see substituteTokens()).
                  Same set of tokens as the per-tournament Info Page editor. */}
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  select size="small" label="Insert token" value="" sx={{ minWidth: 220 }}
                  onChange={e => {
                    const token = e.target.value;
                    if (!token) return;
                    const q = quillRef.current;
                    if (!q) return;
                    const range = q.getSelection(true);
                    const pos = range ? range.index : q.getLength();
                    q.insertText(pos, `{{${token}}}`, 'user');
                    q.setSelection(pos + token.length + 4, 0);
                  }}
                  helperText="Replaced with the live value on each tournament's public page"
                >
                  <MenuItem value="name">Tournament Name</MenuItem>
                  <MenuItem value="date">Tournament Date</MenuItem>
                  <MenuItem value="eventStartTime">Event Start Time</MenuItem>
                  <MenuItem value="eventArrivalTime">Event Arrival Time</MenuItem>
                  <MenuItem value="eventEndTime">Event End Time</MenuItem>
                  <MenuItem value="close_date">Registration Close Date</MenuItem>
                  <MenuItem value="close_time">Registration Close Time</MenuItem>
                  <MenuItem value="refund_date">Refund Cutoff Date</MenuItem>
                  <MenuItem value="refund_time">Refund Cutoff Time</MenuItem>
                  <MenuItem value="late_date">Late Registration Date</MenuItem>
                  <MenuItem value="late_time">Late Registration Time</MenuItem>
                  <MenuItem value="sanctioning_body">Sanctioning Body</MenuItem>
                </TextField>
              </Box>
              <div id="quill-dashboard" style={{ minHeight: 180, background: 'white' }}></div>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button variant="contained" onClick={saveInfoHtml} disabled={savingInfo}>
                  {savingInfo ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                </Button>
                {infoHtml && (
                  <Button variant="outlined" color="error" onClick={async () => {
                    if (!window.confirm('Clear and revert to default?')) return;
                    await setDoc(doc(db, 'settings', 'tournamentInfo'), { html: '' });
                    setInfoHtml('');
                    if (quillRef.current) quillRef.current.setContents([]);
                  }}>Clear</Button>
                )}
              </Box>
            </CardContent>
          </Collapse>
        </Card>

        {/* Tournament list */}
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography fontWeight="bold">
              <EmojiEventsIcon sx={{ mr: 1, color: 'warning.main', verticalAlign: 'middle' }} />
              Tournaments
            </Typography>
          </Box>
          {copyError && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCopyError('')}>
              {copyError}
            </Alert>
          )}
          {tournaments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography color="text.secondary">No tournaments yet.</Typography>
              <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/tournament-admin/tournament/add')}>Add One</Button>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell fontWeight="bold">Tournament</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Divisions</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tournaments.map(t => {
                  const divs = Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]');
                  return (
                    <TableRow key={t.id} sx={{ opacity: t.archived ? 0.4 : (t.active ? 1 : 0.6), bgcolor: t.archived ? 'grey.100' : (t.active ? 'inherit' : 'warning.50') }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {t.name}
                          {!t.active && <Chip label="Draft" size="small" color="warning" sx={{ ml: 1 }} />}
                        </Typography>
                      </TableCell>
                      <TableCell>{fmtDate(t.date)}</TableCell>
                      <TableCell>{t.location || '–'}</TableCell>
                      <TableCell>
                        {divs.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">–</Typography>
                        ) : (
                          <Tooltip title={divs.join(', ')}>
                            <Chip label={`${divs.length} division${divs.length === 1 ? '' : 's'}`} size="small" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        {(() => {
                          const status = getTournamentStatus(t);
                          const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
                          const isCanceled = !t.archived && !['scheduled','pending','complete'].includes(status);
                          return (
                            <FormControl size="small" fullWidth>
                              <Select
                                value={t.tournament_status || status}
                                onChange={async e => {
                                  const val = e.target.value;
                                  const wasCanceled = String(t.tournament_status || '').startsWith('canceled');
                                  const nowCanceled = val.startsWith('canceled');
                                  await updateDoc(doc(db, 'tournaments', t.id), { tournament_status: val });
                                  setTournaments(prev => prev.map(x => x.id === t.id ? { ...x, tournament_status: val } : x));
                                  // Calling a tournament off has to let the courts go, or they
                                  // stay blocked on the calendar for an event nobody is running.
                                  // Archiving already did this; the status dropdown did not.
                                  if (nowCanceled && !wasCanceled) {
                                    try {
                                      const freed = await releaseTournamentCourts(db, t.id);
                                      if (freed) alert(`${freed} court reservation(s) removed from the calendar.`);
                                    } catch (err) {
                                      alert('The tournament was marked cancelled, but its court reservations could not be removed: ' + err.message);
                                    }
                                  } else if (wasCanceled && !nowCanceled) {
                                    // The holds are gone, and rebuilding them needs the waves and
                                    // court assignments — which is what saving the tournament does.
                                    alert('Court reservations were released when this was cancelled. Open and save the tournament to put them back on the calendar.');
                                  }
                                }}
                                renderValue={val => {
                                  const c = STATUS_CONFIG[val] || STATUS_CONFIG.pending;
                                  return <Chip label={c.label} color={c.color} size="small" sx={{ fontWeight: 600 }} />;
                                }}
                                sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                              >
                                <MenuItem value="scheduled">Scheduled</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="complete">Complete</MenuItem>
                                <MenuItem value="canceled-weather">Canceled (Weather)</MenuItem>
                                <MenuItem value="canceled-lowreg">Canceled (Low Reg)</MenuItem>
                                <MenuItem value="canceled-other">Canceled (Other)</MenuItem>
                              </Select>
                            </FormControl>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={copiedId === t.id
                          ? 'Link copied'
                          : `Copy share link: /t/${t.slug || '(no slug — set one in Edit)'}`}>
                          {/* span so the tooltip still shows on the disabled
                              state; a disabled button fires no pointer events. */}
                          <span>
                            <IconButton size="small" disabled={!t.slug}
                              color={copiedId === t.id ? 'success' : 'default'}
                              onClick={() => copyShareLink(t)}>
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t.active ? 'Unpublish' : 'Publish'}>
                          <IconButton size="small" onClick={() => togglePublish(t)}>
                            {t.active ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" color="success" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => navigate(`/tournament-admin/tournament/${t.id}/edit`)}>
                            <EditIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate">
                          <IconButton size="small" onClick={() => navigate(`/tournament-admin/tournament/add?copy=${t.id}`)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.vb_scores_url ? 'Import / Re-import results' : 'Import results from vb-scores'}>
                          <IconButton size="small" onClick={() => openImportDialog(t)}>
                            <EmojiEventsIcon fontSize="small" sx={{ color: t.vb_scores_url ? '#C9A84C' : '#9aa0a6' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={(t.payment_links || []).length
                          ? `Payment links (${(t.payment_links || []).length})`
                          : 'Create a payment link'}>
                          <IconButton size="small" onClick={() => openPayDialog(t)}>
                            <PaymentsIcon fontSize="small"
                              sx={{ color: (t.payment_links || []).length ? '#C9A84C' : '#9aa0a6' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Export ZIP">
                          <IconButton size="small" onClick={() => exportAllCSV(t)}>
                            <DownloadIcon fontSize="small" color="success" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.archived ? 'Already archived' : 'Archive tournament'}>
						<span>
							<IconButton size="small" onClick={() => archiveTournament(t)} disabled={t.archived}>
							<DeleteIcon fontSize="small" color={t.archived ? 'disabled' : 'error'} />
							</IconButton>
						</span>
					    </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </TableContainer>
          )}
        </Card>
      </Container>

      {/* Payment Link Dialog */}
      <Dialog open={payDialog} onClose={() => !payBusy && setPayDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a2940', color: '#fff' }}>
          Payment link — {payTournament?.name || ''}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {payError && <Alert severity="error" sx={{ mb: 2 }}>{payError}</Alert>}

          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            A link anyone can pay without signing in — for a team paying by phone,
            or a walk-up on the morning of. It is tagged as tournament income, so
            it shows up under this tournament in the revenue report.
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 7 }}>
              <FormControl size="small" fullWidth>
                <Select value={payDivision} displayEmpty
                        onChange={e => changePayDivision(e.target.value)}>
                  <MenuItem value="">Whole tournament (no division)</MenuItem>
                  {(payTournament?.divisions || []).map(d => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField size="small" fullWidth label="Amount" type="number"
                         value={payAmount}
                         onChange={e => { setPayAmount(e.target.value); setPayResult(null); }}
                         InputProps={{ startAdornment: <Box component="span" sx={{ mr: 0.5 }}>$</Box> }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField size="small" fullWidth label="Note (optional)"
                         placeholder="Shows on the receipt — e.g. Late entry, Smith/Jones"
                         value={payNote} onChange={e => setPayNote(e.target.value)} />
            </Grid>
          </Grid>

          {payResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {payResult.reused
                ? 'This link already existed at that price, so here it is again.'
                : 'Link created.'}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                  {payResult.url}
                </Typography>
                <Button size="small" variant={payCopied ? 'contained' : 'outlined'}
                        color={payCopied ? 'success' : 'primary'}
                        onClick={() => copyPayLink(payResult.url)}>
                  {payCopied ? 'Copied' : 'Copy'}
                </Button>
              </Box>
            </Alert>
          )}

          {(payTournament?.payment_links || []).length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Links already made</Typography>
              {(payTournament.payment_links || []).map(l => (
                <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5,
                                      borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {l.division || 'Whole tournament'} — ${(l.amountCents / 100).toFixed(2)}
                  </Typography>
                  <Button size="small" onClick={() => copyPayLink(l.url)}>Copy</Button>
                </Box>
              ))}
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                Creating a link for a division that already has one at a different
                price replaces it, and switches the old one off in Stripe.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(false)} disabled={payBusy}>Close</Button>
          <Button variant="contained" onClick={createPaymentLink} disabled={payBusy}>
            {payBusy ? 'Creating…' : 'Create link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Tournament Results Dialog */}
      <Dialog open={importDialog} onClose={() => !importing && setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#1a2940', color: '#fff' }}>
          🏆 Import Results — {importTournament?.name || ''}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
          {importResult && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {importResult.message}
              <Box component="ul" sx={{ mt: 1, pl: 2, mb: 1 }}>
                {(importResult.summary || []).map((s, i) => (
                  <li key={i}>
                    <strong>{s.division}</strong>: {s.teams} team{s.teams !== 1 ? 's' : ''}
                    {s.status ? <Chip label={s.status} size="small" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }} /> : null}
                    {(s.matched != null) && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        · {s.matched} matched{s.unmatched > 0 ? `, ${s.unmatched} unmatched` : ''}{s.ambiguous > 0 ? `, ${s.ambiguous} ambiguous` : ''}
                      </Typography>
                    )}
                  </li>
                ))}
              </Box>
              {importTournament?.id && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined"
                          onClick={() => window.open(`/tournament/${importTournament.id}/results`, '_blank')}>
                    View Results Page
                  </Button>
                  {(importResult.summary || []).some(s => (s.unmatched || 0) + (s.ambiguous || 0) > 0) && (
                    <Button size="small" variant="contained" color="warning"
                            onClick={() => navigate(`/tournament-admin/tournament/${importTournament.id}/match-results`)}>
                      Resolve Matches
                    </Button>
                  )}
                </Box>
              )}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
            Paste the vb-scores tournament URL — all divisions on the page will be imported.
          </Typography>
          <TextField fullWidth size="small" label="vb-scores URL" value={importUrl}
                     onChange={e => setImportUrl(e.target.value)} disabled={importing}
                     placeholder="https://www.vb-scores.com/tournaments/..." />
          {importTournament?.vb_scores_url && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Last imported from this URL — re-importing will overwrite existing results.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)} disabled={importing}>Close</Button>
          <Button onClick={handleImportResults} variant="contained" color="warning"
                  disabled={importing || !importUrl.trim()}>
            {importing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function loadQuill() {
  return new Promise(resolve => {
    if (window.Quill) { resolve(window.Quill); return; }
    if (!document.getElementById('quill-css')) {
      const link = document.createElement('link');
      link.id = 'quill-css'; link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.min.css';
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.min.js';
    script.onload = () => resolve(window.Quill);
    document.head.appendChild(script);
  });
}
